import { useMutation, useQueryClient } from '@tanstack/react-query';
import { HeartCrack } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { VehicleSubscriptionsResponse } from '@//app/api/protected/subscriptions/vehicles';
import type { EnrichedVehicleSubscriptionResponse } from '@//app/api/protected/subscriptions/vehicles/enriched';
import { useAuth } from '@//providers/AuthProvider';
import { useApiBaseUrl } from '@//providers/MarketSettingsProvider';
import { ToastTypeEnum, useToast } from '@//providers/ToastProvider';
import { httpClientDeleteAuthorizedJson } from '../../httpClient';
import { VEHICLE_SUBSCRIPTIONS_ENRICHED_CACHE_KEY } from './useEnrichedVehicleSubscriptions';
import { VEHICLE_SUBSCRIPTIONS_CACHE_KEY } from './useVehicleSubscriptions';

export interface DeleteVehicleSubscriptionVariables {
  id: string;
}

const useDeleteVehicleSubscription = () => {
  const t = useTranslations('other.toast.subscription.vehicle.delete');
  const { decodedToken } = useAuth();
  const apiBaseUrl = useApiBaseUrl();
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const mutation = useMutation<null, Error, DeleteVehicleSubscriptionVariables>({
    mutationFn: ({ id }) =>
      httpClientDeleteAuthorizedJson(`${apiBaseUrl}/api/protected/subscriptions/vehicles/${id}`),
    onSuccess: (_, { id }) => {
      const key = [VEHICLE_SUBSCRIPTIONS_CACHE_KEY, decodedToken?.sub];
      const cache = queryClient.getQueryData<VehicleSubscriptionsResponse>(key);
      if (cache) {
        const nextSubscriptions = cache.response.filter((subscription) => subscription.id !== id);

        queryClient.setQueryData<VehicleSubscriptionsResponse>(key, {
          successful: true,
          response: nextSubscriptions,
        });
      }

      const enrichedVehicleSubscriptionsKey = [
        VEHICLE_SUBSCRIPTIONS_ENRICHED_CACHE_KEY,
        decodedToken?.sub,
      ];
      const enrichedVehicleSubscriptionCache =
        queryClient.getQueryData<EnrichedVehicleSubscriptionResponse>(
          enrichedVehicleSubscriptionsKey,
        );

      if (enrichedVehicleSubscriptionCache) {
        const nextEnrichedSubscriptions =
          enrichedVehicleSubscriptionCache.enrichedSubscriptions.filter(
            (enrichedSubscription) => enrichedSubscription.subscription.id !== id,
          );

        queryClient.setQueryData<EnrichedVehicleSubscriptionResponse>(
          enrichedVehicleSubscriptionsKey,
          {
            enrichedSubscriptions: nextEnrichedSubscriptions,
          },
        );
      }

      addToast({
        type: ToastTypeEnum.Success,
        icon: <HeartCrack aria-hidden="true" />,
        title: t('success.title'),
        description: t('success.description'),
      });
    },
    onError: () => {
      addToast({
        type: ToastTypeEnum.Error,
        title: t('error.title'),
        description: t('error.description'),
      });
    },
  });

  return mutation;
};

export default useDeleteVehicleSubscription;
